import { action } from '@ember/object';
import { trackedSet } from '@ember/reactive/collections';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismInput, PrismModal } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t, tKey } from 'ember-intl';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { BudgetForecastEntry } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { isErrorWithStatusCode } from 'lumen/utils/budget-forecast/data-fetching';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export interface SettingsBudgetForecastModalsModalDuplicateEntrySignature {
  Args: {
    entry: BudgetForecastEntry;
    isDuplicateAsOtherType: boolean;
    isTitleUnique: (title: string) => boolean;
    onClose: () => void;
  };
}

interface DuplicateEntryBody {
  data_type?: 'budget' | 'forecast';
  title: string;
}

export default class SettingsBudgetForecastModalsModalDuplicateEntry extends Component<SettingsBudgetForecastModalsModalDuplicateEntrySignature> {
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked title = '';

  failedTitles = trackedSet<string>();

  duplicateEntryTask = task(async (): Promise<void> => {
    const title = this.title.trim();

    const isTitleValid = title.trim().length > 0 && !this.hasTitleError;
    if (!isTitleValid) {
      return;
    }

    try {
      const data: DuplicateEntryBody = { title: title };
      if (this.args.isDuplicateAsOtherType) {
        data.data_type =
          this.args.entry.dataType === 'budget' ? 'forecast' : 'budget';
      }

      const baseUrl = `/apigateway/v1/app/budgetforecast/entries/${this.args.entry.id}/duplicate`;
      const queryParams = {
        subscription_id: this.hotelAndMarketService.hotel.id!,
      };

      const url = formatUrl(baseUrl, queryParams, this.ajax.host);
      const response = await this.ajax.request<{ entry: { id: string } }>(url, {
        method: 'POST',
        data: data,
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload('budget-forecast/entry', response);
      const newEntry = this.store.peekRecord<BudgetForecastEntry>(
        'budget-forecast/entry',
        response.entry.id,
      );

      if (newEntry) {
        this.#handleDuplicateSuccess(newEntry);
      }
    } catch (error) {
      if (isErrorWithStatusCode(error) && error.status === 409) {
        this.failedTitles.add(title);
        return;
      }

      this.errorTracking.captureException(error);
      void this.alerting.showErrorAlert();
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  get buttonLabel(): string {
    if (this.args.isDuplicateAsOtherType) {
      return this.args.entry.dataType === 'budget'
        ? this.intl.t('budget_forecast.duplicate_as_forecast')
        : this.intl.t('budget_forecast.duplicate_as_budget');
    }

    return this.args.entry.dataType === 'budget'
      ? this.intl.t('budget_forecast.duplicate_budget')
      : this.intl.t('budget_forecast.duplicate_forecast');
  }

  get hasTitleError(): boolean {
    const title = this.title.trim();

    if (!this.args.isDuplicateAsOtherType) {
      return !this.args.isTitleUnique(title);
    }

    return this.failedTitles.has(title);
  }

  get modalTitle(): string {
    if (this.args.isDuplicateAsOtherType) {
      return this.args.entry.dataType === 'budget'
        ? this.intl.t('budget_forecast.duplicate_as_forecast')
        : this.intl.t('budget_forecast.duplicate_as_budget');
    }

    return this.args.entry.dataType === 'budget'
      ? this.intl.t('budget_forecast.duplicate_budget')
      : this.intl.t('budget_forecast.duplicate_forecast');
  }

  #handleDuplicateSuccess(newEntry: BudgetForecastEntry): void {
    const { entry, isDuplicateAsOtherType, onClose } = this.args;
    let message: string;

    if (isDuplicateAsOtherType) {
      const messageKey =
        newEntry.dataType === 'budget'
          ? tKey('budget_forecast.duplicate_as_budget.success')
          : tKey('budget_forecast.duplicate_as_forecast.success');

      message = this.intl.t(messageKey, {
        title: entry.titleWithDefault,
      });
    } else {
      message = this.intl.t('budget_forecast.duplicate.success', {
        title: entry.titleWithDefault,
      });
    }

    this.toast.success(message, { showIcon: true });

    onClose();

    const routeName =
      newEntry.dataType === 'budget'
        ? 'hotel.settings.budget.detail'
        : 'hotel.settings.forecast.detail';

    this.router.transitionTo(routeName, newEntry.id);
  }

  @action
  onInputTitle(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.title = input.value;
  }

  <template>
    <PrismModal
      @isCloseDisabled={{this.duplicateEntryTask.isRunning}}
      @onClose={{@onClose}}
      @title={{this.modalTitle}}
      data-test-modal="duplicate-entry"
    >
      <:default>
        <div class="modal--padded flex">
          <PrismInput
            @errorMessage={{t
              "budget_forecast.create_entry.error.title_not_unique"
            }}
            @hasErrors={{this.hasTitleError}}
            @label={{t "budget_forecast.title"}}
            @onInput={{this.onInputTitle}}
            @placeholder={{t "budget_forecast.add_title"}}
            @required={{true}}
            @requiredErrorMessage={{t "general.required"}}
            @value={{this.title}}
            data-test-field="title"
          />
        </div>
      </:default>
      <:footer>
        <div class="mx-500 flex h-full items-center justify-end gap-200">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{this.duplicateEntryTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
            data-test-button="cancel"
          />
          <PrismButton
            @isDisabled={{this.duplicateEntryTask.isRunning}}
            @isLoading={{this.duplicateEntryTask.isRunning}}
            @label={{this.buttonLabel}}
            @onClick={{this.duplicateEntryTask.perform}}
            data-test-button="duplicate"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
